import { useEffect, useState } from 'react';
import { IAutoCloseSetting, ITicket } from '../types/Ticket/ITicket';
import { ISetStatusProps } from '../types/Ticket/Status/ISetStatusProps';
import { IUserProfile } from '../types/User/IUserProfile';
import { TicketApi } from '../redux/api/TicketApi';
import useRedirectOnClose from './useRedirectOnClose';
import { useNavigate } from 'react-router-dom';
import { useCloseDialog } from '../components/Ticket/TicketDetails/Sections/Tools/Close/CloseDialogContext';
import { useReduxDispatch, useReduxSelector } from '../redux/hooks';
import { setIsClosing } from '../slices/TicketDetailSlice';

interface UseCloseTicketLogicReturn {
    closeTicket: (ticket: ITicket, closeStatusId?: number, autoCloseSettings?: IAutoCloseSetting) => void;
    isClosing: boolean;
    error: string | null;
}

export const useCloseTicketLogic = (profile: IUserProfile): UseCloseTicketLogicReturn => {
    const { isClosing } = useReduxSelector(s => s.ticketDetails);
    const updateState = useReduxDispatch();
    //const [isClosing2, setIsClosing] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [setAssigned, { isLoading }] = TicketApi.useReassignMutation();
    const [setStatus] = TicketApi.useSetStatusMutation();
    //const [configGroupProps, setConfirmDialogProps] = useState<GroupAssignedProps>(null);
    const handleRedirect = useRedirectOnClose();
    const { data: allStatuses } = TicketApi.useGetStatusesQuery();
    const defaultCloseStatus = allStatuses?.find(s => s.Closed_default === true);
    const navigate = useNavigate();
    const { showDialog, hideDialog, showLoading } = useCloseDialog();

    useEffect(() => {
        showLoading(isLoading);
    }, [isLoading]);


    //Close the current ticket and assign the specified status id
    const closeTicket = (ticket: ITicket, statusId?: number, autoCloseSettings?: IAutoCloseSetting) => {
        console.log('profile.Features.Require_Ticket_Ownership_To_Close', profile.Features.Require_Ticket_Ownership_To_Close, ticket.Call_Assignment);

        //Check if the ticket is assigned only to a queue - need to show a dialog to confirm assignment to the current user
        if (ticket.Call_Assignment.length === 1 && ticket.Call_Assignment[0].Access_Level_Id === 6) {
            showDialog({
                show: true, onClose: (yes) => {
                    //Assign to ME
                    if (yes) {
                        setAssigned({ TicketId: ticket.ID, New_Assignees: [profile?.Id] }).unwrap().then(() => {
                            //Close 
                            //setConfirmDialogProps(p => { return { ...p, show: false } })
                            hideDialog();
                            continueClose({ ...ticket, CloseValidationCodes: ticket.CloseValidationCodes?.filter(c => c !== -7) }, statusId, autoCloseSettings);
                        });
                    } else {
                        hideDialog();
                    }
                }
            });
            return
        } else {
            //Require Ownership to close FEATURE
            if (profile.Features.Require_Ticket_Ownership_To_Close && ticket.Call_Assignment.find(a => a.Contact_Id === profile.Id) === undefined) {
                showDialog({
                    message: `${profile?.Phrases.CallPhrase} ownership is required. Would you like to assign the ${profile?.Phrases.CallPhrase} to yourself before closing it?`,
                    show: true, onClose: (yes) => {
                        //Assign to ME
                        if (yes) {
                            setAssigned({ TicketId: ticket.ID, New_Assignees: [profile?.Id] }).unwrap().then(() => {
                                //Close 
                                hideDialog();

                                continueClose({ ...ticket, CloseValidationCodes: ticket.CloseValidationCodes?.filter(c => c !== -7) }, statusId, autoCloseSettings);
                            });
                        } else {
                            hideDialog();
                        }
                    }
                });
                return
            }
        }

        continueClose(ticket, statusId, autoCloseSettings);
    }

    const continueClose = (ticket: ITicket, statusId?: number, autoCloseSettings?: IAutoCloseSetting) => {
        console.warn('continueClose', ticket, statusId, autoCloseSettings, ticket.CloseValidationCodes);
        //if there is no close validation then just close the ticekt otherwise navigate to the close page
        if (ticket.CloseValidationCodes.length === 0) {
            updateState(setIsClosing(true));

            //Check if there are advanced hierarchy options
            let props: ISetStatusProps = {
                Ticket_Id: ticket.ID,
                New_Status_Id: statusId || defaultCloseStatus?.ID,
                Auto_Close_Date: autoCloseSettings?.Enabled && (autoCloseSettings.Auto_Close_Date ? autoCloseSettings.Auto_Close_Date : null),
            } as ISetStatusProps;

            if (profile.Features.Advanced_Heirarchy_Options && ticket.Is_Parent) {
                props.Cascade_To_Children = window.confirm('Do you want to cascade this status change to all children?');
            }

            //DefaULT CLOSE
            setStatus(props).unwrap().then(() => {
                updateState(setIsClosing(false));

                //Close the ticket
                handleRedirect(ticket?.ID);

                setError('Testing');
            }).catch((err) => {
                updateState(setIsClosing(false));

                //if the api returns a 400 error them open the validation popup
                if (err.originalStatus === 400) {
                    navigate(`close/${statusId}`);
                }
            });

            return false;
        }

        navigate(`close/${statusId || ''}`);
    }

    return { closeTicket, isClosing, error };
};

